import {
  ApiErrorEnvelope,
  type ApiError as ApiErrorPayload,
  ApiError as ApiErrorPayloadSchema,
  type CartItem,
  CartQuoteResponse,
  CheckoutCreateResponse,
  CheckoutQuote,
  ClientConfig,
  CustomerOrderView,
  type DeliveryMethod,
  HealthCheckResponse,
  JobManifest,
  type LegalDocumentSlug,
  type OrderDelivery,
  type PillowShape,
  type PillowVariantSelection,
  type ProductCreateRequest,
  ProductCreateResponse,
  ProductPatchResponse,
  type ProjectConfigSize,
  type ProjectSizeCm,
  PublicLegalDocument,
  StaticRectangleAssets,
  type ThreeDPrintCreateRequest,
  UploadCreateResponse,
  VariantPricing,
} from "@pufimi/shared";

// The API origin the browser calls. In the DEV SERVER it is derived from the PAGE's
// OWN host so ONE running stack serves BOTH `localhost` and a LAN host (e.g. a phone on
// the network): the dev API always lives at `<same-host>:8787`, which keeps the page
// and the API SAME-SITE so the `SameSite=Lax` anon-session cookie flows — mixing a
// localhost page with a LAN-host API (or vice-versa) would 403 every job. Gated on
// `MODE === "development"`, so it fires ONLY in the running dev server: under Vitest
// (`MODE === "test"`) and in the production build the value is the build-time
// `VITE_API_URL` (a distinct origin in prod, e.g. api.pufimi.pl), leaving tests + prod
// exactly as before.
export const apiBaseUrl: string =
  import.meta.env.MODE === "development" && typeof window !== "undefined"
    ? `${window.location.protocol}//${window.location.hostname}:8787`
    : (import.meta.env.VITE_API_URL ?? "");

// Discriminated `ApiError` from @pufimi/shared narrows extras off `code` —
// e.g. `error.code === 'file_too_large'` types `error.max_size_mb` as a
// number. The constructor is private so callers can't fabricate one with
// a payload that doesn't match the union.
export class ApiError extends Error {
  readonly status: number;
  readonly payload: ApiErrorPayload;

  constructor(args: { status: number; payload: ApiErrorPayload }) {
    super(args.payload.message ?? args.payload.code);
    this.name = "ApiError";
    this.status = args.status;
    this.payload = args.payload;
  }

  get code(): ApiErrorPayload["code"] {
    return this.payload.code;
  }
}

// The shared Response→`ApiError` mapper. The session JSON API reaches it through
// `request`, and it is exported so the ungated capability-plane callers that run
// their OWN fetch (e.g. `arDelivery.park`, ADR-0035 §3) surface the SAME typed
// `ApiError` — including the `internal_error` fallback for a non-envelope body.
export async function readErrorEnvelope(res: Response): Promise<ApiError> {
  const body: unknown = await res.json().catch(() => undefined);
  const parsed = ApiErrorEnvelope.safeParse(body);
  if (parsed.success) {
    return new ApiError({ status: res.status, payload: parsed.data.error });
  }
  // Body is missing, malformed, or carries an unknown error code. Fall back
  // to `internal_error` so callers always see a parsed-typed payload.
  return new ApiError({
    status: res.status,
    payload: ApiErrorPayloadSchema.parse({
      code: "internal_error",
      message: `API request failed (${res.status})`,
    }),
  });
}

interface RequestInitWithBody extends Omit<RequestInit, "body"> {
  body?: BodyInit;
}

async function request(path: string, init: RequestInitWithBody = {}): Promise<unknown> {
  const res = await fetch(`${apiBaseUrl}${path}`, { credentials: "include", ...init });
  if (!res.ok) throw await readErrorEnvelope(res);
  return res.json();
}

export const api = {
  getHealth: async (): Promise<HealthCheckResponse> => {
    return HealthCheckResponse.parse(await request("/healthz"));
  },
  uploads: {
    create: async (file: File): Promise<UploadCreateResponse> => {
      const form = new FormData();
      form.append("file", file);
      return UploadCreateResponse.parse(await request("/uploads", { method: "POST", body: form }));
    },
  },
  jobs: {
    get: async (jobId: string): Promise<JobManifest> => {
      return JobManifest.parse(await request(`/jobs/${encodeURIComponent(jobId)}`));
    },
  },
  clientConfig: {
    // The public, client-safe app config (ADR-0035 §6, #382): the AR launch-flag
    // state the SPA reads at load to gate the "Zobacz w swoim pokoju" button. No
    // login, no session — a global, non-PII boolean.
    get: async (): Promise<ClientConfig> => {
      return ClientConfig.parse(await request("/client-config"));
    },
  },
  staticRectangles: {
    // Signed URLs for the shared static rectangle GLBs, one per size (ADR-0033
    // §2). Loaded once and reused for every rectangle render — the pet maps on
    // at render time, so size is the only thing that selects the mesh.
    get: async (): Promise<StaticRectangleAssets> => {
      return StaticRectangleAssets.parse(await request("/static/rectangles"));
    },
  },
  cart: {
    // Cart quote (ADR-0054 §2/§3): re-price + re-verify the browser-local cart
    // server-side, returning per-line prices + subtotal + shipping + total. The
    // client sends only `{ productId, quantity }` — never a price. `deliveryMethod`
    // selects which flat rate the `totalCents` reflects (both come back for the
    // live-recompute); a foreign/stale product throws `forbidden` / `not_found`.
    quote: async (
      items: CartItem[],
      deliveryMethod: DeliveryMethod = "paczkomat",
    ): Promise<CartQuoteResponse> => {
      return CartQuoteResponse.parse(
        await request("/cart/quote", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ items, deliveryMethod }),
        }),
      );
    },
  },
  products: {
    // "add more" (ADR-0054 §2, #405): spawn another Product of the same type on the
    // same project, its config copied from a sibling, the buyer's Variant applied. No
    // preview — the server resolves the price + footprint. Returns the new product id
    // the browser-local cart then holds. A foreign/expired project throws forbidden /
    // not_found.
    create: async (input: ProductCreateRequest): Promise<ProductCreateResponse> => {
      return ProductCreateResponse.parse(
        await request("/products", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(input),
        }),
      );
    },
    // Variant edit (ADR-0054 §2, #405): change a cart line's Variant (PATCH), no preview;
    // the server re-resolves the price + footprint. The client sends only the pick — never
    // a price. A foreign/unknown product throws forbidden / not_found.
    patchVariant: async (
      productId: string,
      variant: PillowVariantSelection,
    ): Promise<ProductPatchResponse> => {
      return ProductPatchResponse.parse(
        await request(`/products/${encodeURIComponent(productId)}`, {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ variant }),
        }),
      );
    },
    // Create a 3D-print Product from its (stub) configurator (ADR-0054 §1, #407): the
    // buyer's size + colour-depth pick + a frame, on a project they own. The server
    // resolves the price + footprint; the client sends no price. Returns the new product
    // id the browser-local cart then holds. A foreign/expired project throws.
    createThreeDPrint: async (input: ThreeDPrintCreateRequest): Promise<ProductCreateResponse> => {
      return ProductCreateResponse.parse(
        await request("/products/3d-print", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(input),
        }),
      );
    },
  },
  checkout: {
    // Multi-line guest checkout (ADR-0048 / ADR-0054 §3): re-verify ownership + re-price
    // the cart server-side, upsert the buyer (by email), create one order + N order
    // lines + one Stripe Checkout Session (N product line items + 1 shipping), and get
    // the hosted-page URL to redirect to. Email, the Art. 38 waiver, AND the InPost
    // Delivery (#255) are collected on our page; the client never sends a price.
    create: async (input: {
      items: CartItem[];
      email: string;
      withdrawalWaiverAcknowledged: boolean;
      delivery: OrderDelivery;
    }): Promise<CheckoutCreateResponse> => {
      return CheckoutCreateResponse.parse(
        await request("/checkout", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(input),
        }),
      );
    },
    // The variant + price + rendered thumbnail + committed dimensions a project
    // resolves to, for display before paying (#224 / #228).
    quote: async (projectId: string): Promise<CheckoutQuote> => {
      return CheckoutQuote.parse(await request(`/checkout/${encodeURIComponent(projectId)}`));
    },
    // The catalogue base prices per shape/size for the project's fabric (#228). The
    // preview shows the ABSOLUTE price of the currently-configured pillow in the "Zamów"
    // CTA (art. 4 ustawy o cenach), never only at checkout. Read-only; creates no order.
    variantPricing: async (projectId: string): Promise<VariantPricing> => {
      return VariantPricing.parse(
        await request(`/checkout/${encodeURIComponent(projectId)}/tier-pricing`),
      );
    },
    // Commit the size the buyer chose on the preview (shape + tier + exact
    // dimensions) to the project before checkout loads (#228), returning a fresh
    // quote. Ownership-guarded server-side to the session that owns the project.
    commitSelection: async (
      projectId: string,
      selection: { shape: PillowShape; size: ProjectConfigSize; dimensionsCm: ProjectSizeCm },
    ): Promise<CheckoutQuote> => {
      return CheckoutQuote.parse(
        await request(`/checkout/${encodeURIComponent(projectId)}/selection`, {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(selection),
        }),
      );
    },
  },
  orders: {
    // The public, non-PII Order view (ADR-0051, #314): coarse status + amounts +
    // rendered thumbnail, fetched by the unguessable capability token in the
    // path (no login, no session). A bad/unknown token throws `not_found`.
    view: async (token: string): Promise<CustomerOrderView> => {
      return CustomerOrderView.parse(await request(`/orders/${encodeURIComponent(token)}`));
    },
  },
  legal: {
    // The public read of a legal document (#305): the latest PUBLISHED markdown for
    // the slug (Regulamin / Polityka prywatności). A slug with no published version
    // throws `not_found` — the page renders that as a calm "not yet published"
    // state, not an error.
    get: async (slug: LegalDocumentSlug): Promise<PublicLegalDocument> => {
      return PublicLegalDocument.parse(
        await request(`/legal-documents/${encodeURIComponent(slug)}`),
      );
    },
  },
};
